import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import TimerBar from "@/components/TimerBar";
import WeekCalendar, { startOfWeek, addDays } from "@/components/WeekCalendar";
import TimerListView from "@/components/TimerListView";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, CalendarDays, Palmtree, LayoutGrid, List } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { toast } from "sonner";

export default function TimerPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tags, setTags] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [officeClosures, setOfficeClosures] = useState([]);
  const [entries, setEntries] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [daysToShow, setDaysToShow] = useState(5);
  const [runningEntry, setRunningEntry] = useState(null);
  const [viewMode, setViewMode] = useState(
    user?.default_view === "list" ? "list" : "calendar"
  ); // 'calendar' | 'list'

  // Sync to user preferences after auth resolves
  useEffect(() => {
    if (user?.default_view === "list" || user?.default_view === "calendar") {
      setViewMode(user.default_view);
    }
  }, [user?.id]);

  const loadMeta = useCallback(async () => {
    try {
      const [c, a, t, ph, oc] = await Promise.all([
        api.get("/clients"),
        api.get("/activities"),
        api.get("/tags"),
        api.get("/public-holidays"),
        api.get("/office-closures"),
      ]);
      setClients(c.data);
      setActivities(a.data);
      setTags(t.data);
      setPublicHolidays(ph.data);
      setOfficeClosures(oc.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      const startDate = weekStart.toISOString();
      const endDate = addDays(weekStart, 7).toISOString();
      const { data } = await api.get("/time-entries", {
        params: { start_date: startDate, end_date: endDate, scope: "self" },
      });
      setEntries(data);
      const allRunning = data.find((e) => !e.end);
      if (allRunning) setRunningEntry(allRunning);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [weekStart]);

  // also load running entry across all dates (in case it started before week)
  const loadRunning = useCallback(async () => {
    try {
      const { data } = await api.get("/time-entries", { params: { scope: "self" } });
      const r = data.find((e) => !e.end);
      setRunningEntry(r || null);
    } catch (e) {
      // ignore — handled elsewhere
    }
  }, []);

  useEffect(() => { loadMeta(); loadRunning(); }, [loadMeta, loadRunning]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const onStart = async ({ description, client_ids, activity_id, tag_ids }) => {
    try {
      const payload = {
        description, activity_id,
        client_ids: Array.isArray(client_ids) ? client_ids : [],
        start: new Date().toISOString(),
        end: null,
      };
      if (Array.isArray(tag_ids)) payload.tag_ids = tag_ids;
      const { data } = await api.post("/time-entries", payload);
      setRunningEntry(data);
      window.dispatchEvent(new Event("dtr-running-changed"));
      await loadEntries();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Resume an existing entry: stop any running timer, then start a fresh one
  // inheriting description / client / activity / tags from the source entry.
  const resumeEntry = async (entry) => {
    if (!entry) return;
    try {
      if (runningEntry && runningEntry.id !== entry.id) {
        await api.put(`/time-entries/${runningEntry.id}`, {
          description: runningEntry.description,
          client_ids: runningEntry.client_ids || (runningEntry.client_id ? [runningEntry.client_id] : []),
          activity_id: runningEntry.activity_id,
          tag_ids: runningEntry.tag_ids || [],
          start: runningEntry.start,
          end: new Date().toISOString(),
        });
      }
      await onStart({
        description: entry.description || "",
        client_ids: Array.isArray(entry.client_ids) && entry.client_ids.length
          ? entry.client_ids
          : (entry.client_id ? [entry.client_id] : []),
        activity_id: entry.activity_id || null,
        tag_ids: Array.isArray(entry.tag_ids) ? entry.tag_ids : [],
      });
      toast.success("Timer resumed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Patch a running entry's metadata (description / client / activity / tags)
  // without stopping it. Used by TimerBar's debounced auto-save.
  const patchRunning = async (fields) => {
    if (!runningEntry) return;
    try {
      const fallbackClientIds = Array.isArray(runningEntry.client_ids)
        ? runningEntry.client_ids
        : (runningEntry.client_id ? [runningEntry.client_id] : []);
      const { data } = await api.put(`/time-entries/${runningEntry.id}`, {
        description: fields.description ?? runningEntry.description ?? "",
        client_ids: fields.client_ids !== undefined ? fields.client_ids : fallbackClientIds,
        activity_id: fields.activity_id !== undefined ? fields.activity_id : (runningEntry.activity_id || null),
        tag_ids: fields.tag_ids ?? runningEntry.tag_ids ?? [],
        start: fields.start !== undefined ? fields.start : runningEntry.start,
        end: null,
      });
      setRunningEntry(data);
      window.dispatchEvent(new Event("dtr-running-changed"));
      await loadEntries();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onStop = async (entry) => {
    try {
      const stopClientIds = Array.isArray(entry.client_ids)
        ? entry.client_ids
        : (entry.client_id ? [entry.client_id] : []);
      const { data } = await api.put(`/time-entries/${entry.id}`, {
        description: entry.description,
        client_ids: stopClientIds,
        activity_id: entry.activity_id,
        tag_ids: Array.isArray(entry.tag_ids) ? entry.tag_ids : (runningEntry?.tag_ids || []),
        start: entry.start,
        end: new Date().toISOString(),
      });
      setRunningEntry(null);
      window.dispatchEvent(new Event("dtr-running-changed"));
      await loadEntries();
      toast.success("Time entry saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const createEntry = async (payload) => {
    try {
      await api.post("/time-entries", payload);
      await loadEntries();
      toast.success("Entry added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateEntry = async (id, payload) => {
    try {
      await api.put(`/time-entries/${id}`, payload);
      await loadEntries();
      toast.success("Entry updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteEntry = async (id) => {
    try {
      await api.delete(`/time-entries/${id}`);
      await loadEntries();
      toast.success("Entry deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const weekLabel = useMemo(() => {
    const end = addDays(weekStart, daysToShow - 1);
    const sameMonth = weekStart.getMonth() === end.getMonth();
    const fmt = (d, opts) => d.toLocaleDateString(undefined, opts);
    const s = fmt(weekStart, { month: "short", day: "numeric" });
    const e = sameMonth ? fmt(end, { day: "numeric" }) : fmt(end, { month: "short", day: "numeric" });
    return `${s} – ${e}, ${end.getFullYear()}`;
  }, [weekStart, daysToShow]);

  // Working hours preference
  const workHours = useMemo(() => {
    if (!user) return null;
    const toMin = (s) => { if (!s) return null; const [h, m] = s.split(":").map(Number); return (h||0)*60 + (m||0); };
    const start = toMin(user.working_hours_start);
    const end = toMin(user.working_hours_end);
    if (start == null || end == null) return null;
    return {
      startMin: start,
      endMin: end,
      lunchStartMin: toMin(user.lunch_start),
      lunchEndMin: toMin(user.lunch_end),
    };
  }, [user]);

  // Week total + average per worked day, plus a live "Today's total" that
  // ticks up while a timer is running.
  const [liveTick, setLiveTick] = useState(0);

  // Refs keep the latest values available inside the interval callback so
  // the auto-stop guard always sees fresh data (no stale-closure risk).
  const entriesRef = useRef(entries);
  const runningEntryRef = useRef(runningEntry);
  const autoStopFiredRef = useRef(null);
  useEffect(() => { entriesRef.current = entries; }, [entries]);
  useEffect(() => { runningEntryRef.current = runningEntry; }, [runningEntry]);

  useEffect(() => {
    if (!runningEntry) { autoStopFiredRef.current = null; return; }
    // Reset the fire guard whenever a fresh timer starts.
    if (autoStopFiredRef.current && autoStopFiredRef.current !== runningEntry.id) {
      autoStopFiredRef.current = null;
    }
    const id = setInterval(() => {
      setLiveTick((t) => t + 1);
      checkAutoStop();
    }, 1000);
    // Run immediately as well so we catch entries that were already past due
    // when the timer was launched or entries loaded.
    checkAutoStop();
    return () => clearInterval(id);
  }, [runningEntry?.id]);

  // Re-check whenever the entries list changes (a newly-added pre-booked item
  // might already overlap the running timer).
  useEffect(() => { checkAutoStop(); }, [entries]);

  const checkAutoStop = () => {
    const running = runningEntryRef.current;
    if (!running) return;
    if (autoStopFiredRef.current === running.id) return;

    const now = new Date();
    const start = new Date(running.start);

    // 1) Overlap — if any *other* entry on the same calendar day starts after
    // the running timer and its start has been reached, cut the timer off at
    // that entry's start so the pre-booked item is preserved intact.
    const startDayKey = start.toDateString();
    let overlap = null;
    for (const e of entriesRef.current) {
      if (e.id === running.id) continue;
      const es = new Date(e.start);
      if (isNaN(es.getTime())) continue;
      if (es.toDateString() !== startDayKey) continue;
      if (es <= start) continue;              // must start AFTER the timer began
      if (now < es) continue;                 // hasn't been reached yet
      if (!overlap || es < overlap.start) overlap = { entry: e, start: es };
    }
    if (overlap) {
      autoStopFiredRef.current = running.id;
      autoStopRunning(overlap.start.toISOString(), "overlap");
      return;
    }

    // 2) Past 9 PM and ≥ 1 hour elapsed. Only fires if the timer *started*
    // before 9 PM (a legit start-late timer shouldn't be clobbered).
    const nine = new Date(now); nine.setHours(21, 0, 0, 0);
    const elapsedMs = now - start;
    if (now > nine && start < nine && elapsedMs >= 60 * 60 * 1000) {
      autoStopFiredRef.current = running.id;
      autoStopRunning(nine.toISOString(), "past_9pm");
    }
  };

  const autoStopRunning = async (endIso, reason) => {
    if (!runningEntry) return;
    try {
      const fallbackClientIds = Array.isArray(runningEntry.client_ids)
        ? runningEntry.client_ids
        : (runningEntry.client_id ? [runningEntry.client_id] : []);
      await api.put(`/time-entries/${runningEntry.id}`, {
        description: runningEntry.description || "",
        client_ids: fallbackClientIds,
        activity_id: runningEntry.activity_id || null,
        tag_ids: Array.isArray(runningEntry.tag_ids) ? runningEntry.tag_ids : [],
        leave_type: runningEntry.leave_type || null,
        start: runningEntry.start,
        end: endIso,
        auto_stopped_reason: reason,
      });
      setRunningEntry(null);
      window.dispatchEvent(new Event("dtr-running-changed"));
      await loadEntries();
      // Silent for overlap (expected behaviour). Warn only for the 9 PM guard
      // where the user genuinely may have forgotten to stop.
      if (reason === "past_9pm") {
        toast.warning(
          "Timer auto-stopped at 9 PM — looked like you forgot to stop it. Please check the finish time.",
          { duration: 8000 },
        );
      }
    } catch (e) {
      autoStopFiredRef.current = null;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const { weekTotalLabel, weekAvgLabel, todayTotalLabel } = useMemo(() => {
    let total = 0;
    let todayTotal = 0;
    const daysWorked = new Set();
    const todayKey = new Date().toDateString();
    for (const e of entries) {
      if (!e.end) continue;
      const dur = e.duration_seconds || 0;
      total += dur;
      const dKey = new Date(e.start).toDateString();
      daysWorked.add(dKey);
      if (dKey === todayKey) todayTotal += dur;
    }
    // Live add — running entry counted only if started today.
    if (runningEntry && new Date(runningEntry.start).toDateString() === todayKey) {
      const elapsed = Math.max(0, Math.floor((Date.now() - new Date(runningEntry.start).getTime()) / 1000));
      todayTotal += elapsed;
    }
    const fmt = (s) => `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60).toString().padStart(2, "0")}m`;
    const fmtHMS = (s) => {
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
    };
    return {
      weekTotalLabel: fmt(total),
      weekAvgLabel: fmt(daysWorked.size > 0 ? Math.floor(total / daysWorked.size) : 0),
      todayTotalLabel: fmtHMS(todayTotal),
    };
  }, [entries, runningEntry, liveTick]);

  // Add Leave dialog state
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: "annual",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    partial: false,
    start_time: "09:00",
    end_time: "17:00",
    description: "",
  });

  const submitLeave = async () => {
    try {
      const startDate = new Date(leaveForm.start_date + "T00:00:00");
      const endDate = new Date(leaveForm.end_date + "T00:00:00");
      const dayCount = Math.max(1, Math.round((endDate - startDate) / 86400000) + 1);
      // For full-day leave use the user's saved working hours; for partial use form times
      const startTime = leaveForm.partial
        ? leaveForm.start_time
        : (user?.working_hours_start || "09:00");
      const endTime = leaveForm.partial
        ? leaveForm.end_time
        : (user?.working_hours_end || "17:00");
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      let added = 0;
      let skipped = 0;
      for (let i = 0; i < dayCount; i++) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        // Skip weekends (Sat=6, Sun=0) — leave only lands on standard working days.
        const dow = d.getDay();
        if (dow === 0 || dow === 6) { skipped++; continue; }
        const s = new Date(d); s.setHours(sh || 0, sm || 0, 0, 0);
        const e = new Date(d); e.setHours(eh || 0, em || 0, 0, 0);
        await api.post("/time-entries", {
          description: leaveForm.description || "",
          client_id: null,
          activity_id: null,
          leave_type: leaveForm.leave_type,
          start: s.toISOString(),
          end: e.toISOString(),
        });
        added++;
      }
      setLeaveOpen(false);
      await loadEntries();
      if (added === 0) {
        toast.warning("No working days in that range — leave was not added.");
      } else {
        const skipNote = skipped > 0 ? ` (${skipped} weekend day${skipped > 1 ? "s" : ""} skipped)` : "";
        toast.success(added > 1 ? `Leave added for ${added} days${skipNote}` : `Leave added${skipNote}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <TimerBar
        clients={clients}
        activities={activities}
        tags={tags}
        onStart={onStart}
        runningEntry={runningEntry}
        onStop={onStop}
        onPatchRunning={patchRunning}
        onCreateClient={!isAdmin ? null : async ({ name, color }) => {
          try {
            const { data } = await api.post("/clients", { name, color });
            setClients((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success("Client created");
            return data;
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return null;
          }
        }}
        onCreateActivity={!isAdmin ? null : async ({ name, color }) => {
          try {
            const { data } = await api.post("/activities", { name, color });
            setActivities((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success("Activity created");
            return data;
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return null;
          }
        }}
        onManualCreate={async (payload) => {
          try {
            await api.post("/time-entries", payload);
            await loadEntries();
            toast.success("Entry added");
            return true;
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            return false;
          }
        }}
      />

      {/* Sub header */}
      <div className="h-14 px-6 border-b border-slate-200 flex items-center gap-3 bg-white">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
          data-testid="week-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="px-3 h-9 rounded-md border border-slate-200 flex items-center gap-2 text-sm">
          <CalendarDays className="h-4 w-4 text-slate-400" />
          <span data-testid="week-label">{weekLabel}</span>
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
          data-testid="week-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          onClick={() => setWeekStart(startOfWeek(new Date()))}
          className="h-8 px-3 rounded-md hover:bg-slate-100 text-sm text-slate-600"
          data-testid="week-today"
        >
          Today
        </button>

        <div className="ml-4 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 uppercase tracking-widest text-[10px]">Today</span>
            <span className="timer-font font-semibold text-[#145c8c] tabular-nums" data-testid="today-total">
              {todayTotalLabel}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 uppercase tracking-widest text-[10px]">Week total</span>
            <span className="timer-font font-semibold text-slate-700" data-testid="week-total">
              {weekTotalLabel}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 uppercase tracking-widest text-[10px]">Avg / day</span>
            <span className="timer-font font-semibold text-slate-700" data-testid="week-avg">
              {weekAvgLabel}
            </span>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="add-leave-button">
                <Palmtree className="h-4 w-4 mr-1" /> Add Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add leave</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Type</Label>
                  <Select value={leaveForm.leave_type}
                          onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annual">Annual Leave</SelectItem>
                      <SelectItem value="personal">Personal Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">From</Label>
                    <DatePicker value={leaveForm.start_date}
                                onChange={(v) => setLeaveForm({ ...leaveForm, start_date: v, end_date: leaveForm.end_date < v ? v : leaveForm.end_date })}
                                className="mt-1 w-full" testId="leave-start-date" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">To</Label>
                    <DatePicker value={leaveForm.end_date}
                                onChange={(v) => setLeaveForm({ ...leaveForm, end_date: v })}
                                className="mt-1 w-full" testId="leave-end-date" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={leaveForm.partial}
                         onChange={(e) => setLeaveForm({ ...leaveForm, partial: e.target.checked })}
                         data-testid="leave-partial-toggle" />
                  Partial day (specify hours)
                </label>
                {leaveForm.partial && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Start time</Label>
                      <TimePicker value={leaveForm.start_time}
                                  onChange={(v) => setLeaveForm({ ...leaveForm, start_time: v })}
                                  className="mt-1 w-full" testId="leave-start-time" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">End time</Label>
                      <TimePicker value={leaveForm.end_time}
                                  onChange={(v) => setLeaveForm({ ...leaveForm, end_time: v })}
                                  className="mt-1 w-full" testId="leave-end-time" />
                    </div>
                  </div>
                )}
                {!leaveForm.partial && workHours && (
                  <p className="text-xs text-slate-400">
                    Full day uses your working hours ({user.working_hours_start} – {user.working_hours_end}).
                  </p>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Note (optional)</Label>
                  <Input value={leaveForm.description}
                         onChange={(e) => setLeaveForm({ ...leaveForm, description: e.target.value })}
                         className="mt-1" placeholder="e.g. family trip"
                         data-testid="leave-description" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLeaveOpen(false)}>Cancel</Button>
                <Button onClick={submitLeave} className="text-white" style={{ background: "#145c8c" }}
                        data-testid="leave-save-button">Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Select value={String(daysToShow)} onValueChange={(v) => setDaysToShow(Number(v))}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="days-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 days view</SelectItem>
              <SelectItem value="7">7 days view</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center h-9 rounded-md border border-slate-200 overflow-hidden"
               data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode("calendar")}
              className={`h-full px-3 text-xs flex items-center gap-1.5 transition-colors ${
                viewMode === "calendar" ? "bg-[#145c8c] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="view-mode-calendar"
              title="Calendar view"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Calendar
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`h-full px-3 text-xs flex items-center gap-1.5 transition-colors border-l border-slate-200 ${
                viewMode === "list" ? "bg-[#145c8c] text-white border-[#145c8c]" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="view-mode-list"
              title="List view"
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
          </div>
        </div>
      </div>

      {viewMode === "calendar" ? (
        <WeekCalendar
          entries={entries}
          clients={clients}
          activities={activities}
          tags={tags}
          publicHolidays={publicHolidays}
          officeClosures={officeClosures}
          runningEntry={runningEntry}
          onCreate={createEntry}
          onUpdate={updateEntry}
          onDelete={deleteEntry}
          onResume={resumeEntry}
          weekStart={weekStart}
          daysToShow={daysToShow}
          workHours={workHours}
          workingDays={Array.isArray(user?.working_days) && user.working_days.length
            ? user.working_days
            : [1, 2, 3, 4, 5]}
          onCreateClient={!isAdmin ? null : async ({ name, color }) => {
            try {
              const { data } = await api.post("/clients", { name, color });
              setClients((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
              toast.success("Client created");
              return data;
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              return null;
            }
          }}
          onCreateActivity={!isAdmin ? null : async ({ name, color }) => {
            try {
              const { data } = await api.post("/activities", { name, color });
              setActivities((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
              toast.success("Activity created");
              return data;
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              return null;
            }
          }}
        />
      ) : (
        <TimerListView
          entries={entries}
          clients={clients}
          activities={activities}
          tags={tags}
          onCreate={createEntry}
          onEdit={updateEntry}
          onDelete={deleteEntry}
          onResume={resumeEntry}
          onCreateClient={!isAdmin ? null : async ({ name, color }) => {
            try {
              const { data } = await api.post("/clients", { name, color });
              setClients((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
              toast.success("Client created");
              return data;
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              return null;
            }
          }}
          onCreateActivity={!isAdmin ? null : async ({ name, color }) => {
            try {
              const { data } = await api.post("/activities", { name, color });
              setActivities((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
              toast.success("Activity created");
              return data;
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              return null;
            }
          }}
        />
      )}
    </div>
  );
}
